import type { IntlShape } from 'react-intl';
import { createSelector } from 'reselect';

import type { Filter, FilterValue } from 'components/common/FiltersHeader/types';

import {
  ASSIGNEE,
  ASSIGNEE_USERS,
  LINKED_INTERVENTION,
  NOT_EXIST,
  NOT_EXIST_LABEL_DESCRIPTOR,
  STATUS,
} from 'constants/FilterConstants';

import type { MayBe } from 'modules/global/typing/common/common';
import { LeadStatusEnum } from 'modules/global/typing/enums/leads.enum';
import { getByIds, getUIState } from 'modules/leads/selectors/core';
import { getDealerDefaultLanguage, getSelectedDealerId } from 'selectors/dealerSelectors';
import { getProps, getRoot } from 'utils/selectors';

const getState = createSelector(getUIState, ({ dashboard }) => dashboard);

const getDashboardIds = createSelector(getState, ({ ids }) => ids);

export const getDashboardLeads = createSelector(getRoot, getDashboardIds, getByIds);

export const getPage = createSelector(getState, ({ page }) => page);

export const isLoading = createSelector(getState, ({ loading }) => loading);

export const getFilters = createSelector(getState, ({ filters }) => filters);

const isNotAssigned = createSelector(getFilters, ({ notExist }) => notExist.includes(ASSIGNEE));

export const getParams = createSelector(
  getFilters,
  getPage,
  getSelectedDealerId,
  getDealerDefaultLanguage,
  (filters, { number: page, size }, dealerId, language) => ({
    ...filters,
    dealerId,
    page,
    size,
    language,
  })
);

const getAssignedUsers = createSelector(getFilters, filters => filters[ASSIGNEE_USERS]);
export const getAssignedUserIds = createSelector(getAssignedUsers, assignedUsers => assignedUsers.map(({ id }) => id));

const getFormattedAssignee = createSelector(
  getProps<IntlShape>,
  getAssignedUsers,
  isNotAssigned,
  (intl, assignee, notAssigned): MayBe<Filter> => {
    if (!notAssigned && assignee.length === 0) {
      return undefined;
    }

    const unassignedValues: Array<FilterValue> = notAssigned
      ? [
        {
          value: ASSIGNEE,
          name: intl.formatMessage(NOT_EXIST_LABEL_DESCRIPTOR[ASSIGNEE]),
          className: 'default',
          filterId: NOT_EXIST,
        },
      ]
      : [];

    return {
      id: ASSIGNEE_USERS,
      name: intl.formatMessage({ id: 'customSteps.assigned', defaultMessage: 'Assigned' }),
      values: [
        ...unassignedValues,
        ...assignee.map(({ id, displayName }) => ({ value: id, name: displayName, className: 'default' })),
      ],
    };
  }
);

const formatYesNo = (isYes: boolean, intl: IntlShape): string =>
  (isYes
    ? intl.formatMessage({ id: 'yes', defaultMessage: 'Yes' })
    : intl.formatMessage({ id: 'no', defaultMessage: 'No' }));

const getFormattedDirectMessage = createSelector(
  getProps<IntlShape>,
  getFilters,
  (intl, { linkedIntervention }): MayBe<Filter> => {
    if (typeof linkedIntervention !== 'boolean') {
      return undefined;
    }

    return {
      id: LINKED_INTERVENTION,
      name: intl.formatMessage({ id: 'page.leads.dashboard.table.directMessage', defaultMessage: 'Direct Message' }),
      values: [{ value: linkedIntervention, name: formatYesNo(linkedIntervention, intl) }],
    };
  }
);

const getFormattedHandled = createSelector(
  getProps<IntlShape>,
  getFilters,
  (intl, { status }): MayBe<Filter> => {
    if (status.length === 0) {
      return undefined;
    }

    return {
      id: STATUS,
      name: intl.formatMessage({ id: 'page.leads.dashboard.table.handled', defaultMessage: 'Handled' }),
      values: status.map((leadStatus: LeadStatusEnum) => ({
        value: leadStatus,
        name: formatYesNo(leadStatus === LeadStatusEnum.HANDLED, intl),
      })),
    };
  }
);

export const getFormattedFilters = createSelector(
  getFormattedAssignee,
  getFormattedDirectMessage,
  getFormattedHandled,
  (...formattedFilters) => formattedFilters.filter((filter): filter is Filter => Boolean(filter))
);

export const getFilterCount = createSelector(getFormattedFilters, filters =>
  filters.reduce((acc, { values }) => acc + values.length, 0));
